import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Send, Mail, Clock, AlertCircle, CheckCircle2 } from "lucide-react";
import Seo, { breadcrumbLd, organizationLd } from "../components/Seo";
import { site } from "../mock";
import { track } from "../analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/;

const TOPICS = [
  { id: "general", label: "General question" },
  { id: "bug", label: "Something is broken" },
  { id: "partnership", label: "Partnership or brand" },
  { id: "press", label: "Press or media" },
  { id: "other", label: "Something else" },
];

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "general",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.name.trim().length < 2) {
      setError("Please tell us your name.");
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      setError("Please enter a valid email address so we can reply.");
      return;
    }
    if (form.message.trim().length < 10) {
      setError("Please write a little more so we can actually help.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/contact`, form);
      setDone(r.data);
      track("contact_message_sent", { label: form.subject });
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          `Could not send the message. Please email ${site.email} instead.`
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact InZoRAH | Ask us anything"
        description="Send the InZoRAH team a message about the ingredient app, a bug, a partnership or press. We reply to every email within two business days."
        path="/contact"
        keywords="contact InZoRAH, InZoRAH support, ingredient app support"
        jsonLd={[
          organizationLd,
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
        ]}
      />
      <div className="blog-shell" data-testid="contact-page">
        <header className="blog-top">
          <Link to="/" className="blog-brand">
            InZoRAH
          </Link>
          <nav aria-label="Site navigation">
            <Link to="/ingredients">Ingredients A-Z</Link>
            <Link to="/legal">Legal</Link>
          </nav>
        </header>

        <main className="blog-main blog-article">
          <p className="blog-eyebrow">Contact</p>
          <h1 className="post-h1">Talk to a real person</h1>
          <p className="blog-intro">
            Questions about an ingredient, a scan that looked wrong, a partnership or a
            press request — write to us here and it lands straight in our inbox. No
            ticket maze, no bots.
          </p>

          <div className="dd-cards">
            <section className="dd-card">
              <span className="dd-ico">
                <Clock size={17} aria-hidden="true" />
              </span>
              <h2>How fast we reply</h2>
              <p>
                Every message is answered within two business days, usually the same day.
                You will get the reply at the email address you give below.
              </p>
            </section>
            <section className="dd-card">
              <span className="dd-ico">
                <Mail size={17} aria-hidden="true" />
              </span>
              <h2>Prefer your own email app?</h2>
              <p>
                Write to <a href={`mailto:${site.email}`}>{site.email}</a> and we will pick
                it up there.
              </p>
              <p className="dd-note">
                To delete your account or data, use the{" "}
                <Link to="/data-deletion">data deletion page</Link> instead — it is faster.
              </p>
            </section>
          </div>

          <h2 className="dd-h2">Send a message</h2>
          {done ? (
            <div className="dd-done" data-testid="contact-success">
              <CheckCircle2 size={20} aria-hidden="true" />
              <div>
                <b>Message sent</b>
                <p>{done.message}</p>
                <p className="dd-ticket">
                  Reference: <code>{done.ref}</code>
                </p>
              </div>
            </div>
          ) : (
            <form className="dd-form" onSubmit={submit} noValidate data-testid="contact-form">
              <div className="dd-row">
                <label className="dd-field">
                  <span>Your name *</span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jane Doe"
                    autoComplete="name"
                    data-testid="contact-name-input"
                    required
                  />
                </label>
                <label className="dd-field">
                  <span>Your email address *</span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@email.com"
                    autoComplete="email"
                    data-testid="contact-email-input"
                    required
                  />
                  <em>We only use it to reply to you.</em>
                </label>
              </div>

              <label className="dd-field">
                <span>What is this about?</span>
                <select
                  value={form.subject}
                  onChange={set("subject")}
                  data-testid="contact-subject-select"
                >
                  {TOPICS.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="dd-field">
                <span>Your message *</span>
                <textarea
                  rows="6"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us what is going on, and include the product or ingredient name if it helps."
                  data-testid="contact-message-input"
                />
              </label>

              {error && (
                <p className="dd-error" data-testid="contact-error">
                  <AlertCircle size={14} aria-hidden="true" /> {error}
                </p>
              )}

              <div className="dd-actions">
                <button
                  type="submit"
                  className="dd-submit"
                  disabled={busy}
                  data-testid="contact-submit-button"
                >
                  <Send size={16} aria-hidden="true" />
                  {busy ? "Sending…" : "Send message"}
                </button>
              </div>
              <p className="dd-fine">
                Sending this form does not create an account. See our{" "}
                <Link to="/legal/privacy">Privacy Policy</Link> for what we store and why.
              </p>
            </form>
          )}
        </main>

        <footer className="blog-foot">
          KYSolutions LLC · <Link to="/legal/privacy">Privacy</Link> ·{" "}
          <a
            className="inz-support-link"
            href="https://royalelite.support"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="support-portal-link"
          >
            Support
          </a>{" "}
          ·{" "}
          <Link to="/legal/terms">Terms</Link> · <Link to="/">Home</Link> ·{" "}
          <Link to="/admin/login" rel="nofollow">
            Admin
          </Link>
        </footer>
      </div>
    </>
  );
};

export default Contact;
